package com.evoapps.evoshift.domain.usecase.user

import com.evoapps.evoshift.domain.repository.ProfilePhotoRepository

/** The limits of an uploaded profile photo (SH-94); storage.rules enforce the size too. */
object ProfilePhoto {
    /** The uploaded photo is a square of this many pixels. */
    const val SIDE = 512

    /** JPEG quality of the uploaded photo, 0..100. */
    const val JPEG_QUALITY = 85

    /** storage.rules accept `users/{uid}/avatar.jpg` below 1 MB. */
    const val MAX_UPLOAD_BYTES = 1024 * 1024

    /** A picked file larger than this is refused before decoding (camera photos are 2-12 MB). */
    const val MAX_INPUT_BYTES = 30 * 1024 * 1024

    /** A picked image with more pixels than this is refused before decoding (memory). */
    const val MAX_INPUT_PIXELS = 100_000_000L
}

/** The encoded photo is over [ProfilePhoto.MAX_UPLOAD_BYTES] (storage.rules would refuse it). */
class ProfilePhotoTooLargeException(val bytes: Int) : Exception("Profile photo too large: $bytes bytes")

/**
 * Uploads the person's cropped photo, then points their public profile at it. Returns the new
 * `photoUrl`. A failed profile update leaves the uploaded file, which the next upload replaces
 * (and account deletion removes).
 */
class UploadProfilePhotoUseCase(private val repository: ProfilePhotoRepository) {
    suspend operator fun invoke(uid: String, jpeg: ByteArray, onProgress: (Float) -> Unit = {}): Result<String> {
        if (jpeg.isEmpty() || jpeg.size >= ProfilePhoto.MAX_UPLOAD_BYTES) {
            return Result.failure(ProfilePhotoTooLargeException(jpeg.size))
        }
        val url = repository.upload(uid, jpeg, onProgress).getOrElse { return Result.failure(it) }
        return repository.setPhotoUrl(uid, url).map { url }
    }
}

/**
 * "Remove photo": deletes the uploaded file, then clears `photoUrl`, so the avatar shows the
 * initials. Also clears a Google account photo (there is no file to delete then). Deleting first
 * means a failure never leaves the profile pointing at a photo that is half gone the other way
 * round: at worst the profile points at a deleted file, which shows as initials too.
 */
class RemoveProfilePhotoUseCase(private val repository: ProfilePhotoRepository) {
    suspend operator fun invoke(uid: String): Result<Unit> {
        repository.delete(uid).onFailure { return Result.failure(it) }
        return repository.setPhotoUrl(uid, null)
    }
}
